import React from "react";
import { TextField, TextArea } from "../../../common/FormFields";
import VideoFileInput from "../../../common/Inputs/VideoFileInput";
import ShortDescriptionEditor from "../../../common/RichTextEditor/ShortDescriptionEditor";
import { SaveModal } from "../../../common/Modals";

/**
 * VideoFormFields - Video form fields component with all fields from old implementation
 *
 * Key improvements:
 * - Clean component structure
 * - All original fields preserved
 * - Better prop handling
 * - Optimized rendering
 */
const VideoFormFields = ({
  mode = "add", // "add" or "edit"
  preview,
  videoKey = undefined,
  onDeleteClick,
  onFileChange,
  fileAccept,
  fileCapture,
  idValue,
  onIdChange,
  videoUrl,
  onVideoUrlChange,
  shortDescription,
  onShortDescriptionChange,
  onShortDescriptionDirtyChange,
  maxSizeMb = 250,
  canEdit = true,
  saveModal = {
    isOpen: false,
    onClose: () => {},
    onSave: () => {},
    title: "Save",
    subtitle: "",
    editorName: "",
    comment: "",
    onCommentChange: () => {},
    isLoading: false,
    showLoader: true,
  },
}) => {
  const handleOpenVideoUrl = () => {
    try {
      let url = (videoUrl || "").trim();
      if (!url) return;
      // Add protocol if missing (assume https)
      if (!/^https?:\/\//i.test(url) && !/^blob:|^data:|^\//i.test(url)) {
        url = `https://${url}`;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {}
  };
  return (
    <div className="p-6">
      <div className="video-upload-container">
        <div className="" style={{ padding: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ fontSize: "12px", fontWeight: "600" }}>
              Video
              <span className="required" style={{ marginLeft: "0.5px" }}>
                *
              </span>
            </h3>
            <div style={{ display: "flex", alignItems: "center" }}>
              <label
                htmlFor="fileInput"
                className="icon-button"
                title={
                  !canEdit
                    ? "You do not have permission to upload videos."
                    : "Change"
                }
                style={{
                  opacity: !canEdit ? 0.5 : 1,
                  cursor: !canEdit ? "not-allowed" : "pointer",
                  pointerEvents: !canEdit ? "none" : "auto",
                }}
              >
                <svg
                  width="17"
                  height="15"
                  viewBox="0 0 17 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    backgroundColor: "#e8f1f8",
                    padding: "5px",
                    borderRadius: "50%",
                    cursor: !canEdit ? "not-allowed" : "pointer",
                    marginTop: "5px",
                  }}
                >
                  <path
                    d="M1 9.45021C1.13261 9.45021 1.25979 9.50289 1.35355 9.59666C1.44732 9.69042 1.5 9.8176 1.5 9.95021V12.4502C1.5 12.7154 1.60536 12.9698 1.79289 13.1573C1.98043 13.3449 2.23478 13.4502 2.5 13.4502H14.5C14.7652 13.4502 15.0196 13.3449 15.2071 13.1573C15.3946 12.9698 15.5 12.7154 15.5 12.4502V9.95021C15.5 9.8176 15.5527 9.69042 15.6464 9.59666C15.7402 9.50289 15.8674 9.45021 16 9.45021C16.1326 9.45021 16.2598 9.50289 16.3536 9.59666C16.4473 9.69042 16.5 9.8176 16.5 9.95021V12.4502C16.5 12.9806 16.2893 13.4894 15.9142 13.8644C15.5391 14.2395 15.0304 14.4502 14.5 14.4502H2.5C1.96957 14.4502 1.46086 14.2395 1.08579 13.8644C0.710714 13.4894 0.5 12.9806 0.5 12.4502V9.95021C0.5 9.8176 0.552678 9.69042 0.646447 9.59666C0.740215 9.50289 0.867392 9.45021 1 9.45021Z"
                    fill="#0D6EFD"
                  />
                  <path
                    d="M8.146 0.69621C8.19245 0.649647 8.24762 0.612704 8.30837 0.587498C8.36911 0.562291 8.43423 0.549316 8.5 0.549316C8.56577 0.549316 8.63089 0.562291 8.69163 0.587498C8.75238 0.612704 8.80755 0.649647 8.854 0.69621L11.854 3.69621C11.9479 3.7901 12.0006 3.91743 12.0006 4.05021C12.0006 4.18299 11.9479 4.31032 11.854 4.40421C11.7601 4.4981 11.6328 4.55084 11.5 4.55084C11.3672 4.55084 11.2399 4.4981 11.146 4.40421L9 2.25721V11.0502C9 11.1828 8.94732 11.31 8.85355 11.4038C8.75979 11.4975 8.63261 11.5502 8.5 11.5502C8.36739 11.5502 8.24021 11.4975 8.14645 11.4038C8.05268 11.31 8 11.1828 8 11.0502V2.25721L5.854 4.40421C5.80751 4.4507 5.75232 4.48757 5.69158 4.51273C5.63084 4.53789 5.56574 4.55084 5.5 4.55084C5.43426 4.55084 5.36916 4.53789 5.30842 4.51273C5.24768 4.48757 5.19249 4.4507 5.146 4.40421C5.09951 4.35772 5.06264 4.30253 5.03748 4.24179C5.01232 4.18105 4.99937 4.11595 4.99937 4.05021C4.99937 3.98447 5.01232 3.91937 5.03748 3.85863C5.06264 3.79789 5.09951 3.7427 5.146 3.69621L8.146 0.69621Z"
                    fill="#0D6EFD"
                  />
                </svg>
              </label>
              <button
                type="button"
                className="icon-button"
                title={
                  !canEdit
                    ? "You do not have permission to delete videos."
                    : "Delete"
                }
                onClick={onDeleteClick}
                disabled={!canEdit}
                style={{
                  background: "none",
                  border: "none",
                  opacity: !canEdit ? 0.5 : 1,
                  cursor: !canEdit ? "not-allowed" : "pointer",
                  pointerEvents: !canEdit ? "none" : "auto",
                }}
              >
                <svg
                  width="13"
                  height="15"
                  viewBox="0 0 13 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    backgroundColor: "#e8f1f8",
                    padding: "5px",
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                >
                  <path
                    d="M4.3125 5.75C4.42853 5.75 4.53981 5.79609 4.62186 5.87814C4.70391 5.96019 4.75 6.07147 4.75 6.1875V11.4375C4.75 11.5535 4.70391 11.6648 4.62186 11.7469C4.53981 11.8289 4.42853 11.875 4.3125 11.875C4.19647 11.875 4.08519 11.8289 4.00314 11.7469C3.92109 11.6648 3.875 11.5535 3.875 11.4375V6.1875C3.875 6.07147 3.92109 5.96019 4.00314 5.87814C4.08519 5.79609 4.19647 5.75 4.3125 5.75ZM6.5 5.75C6.61603 5.75 6.72731 5.79609 6.80936 5.87814C6.89141 5.96019 6.9375 6.07147 6.9375 6.1875V11.4375C6.9375 11.5535 6.89141 11.6648 6.80936 11.7469C6.72731 11.8289 6.61603 11.875 6.5 11.875C6.38397 11.875 6.27269 11.8289 6.19064 11.7469C6.10859 11.6648 6.0625 11.5535 6.0625 11.4375V6.1875C6.0625 6.07147 6.10859 5.96019 6.19064 5.87814C6.27269 5.79609 6.38397 5.75 6.5 5.75ZM9.125 6.1875C9.125 6.07147 9.07891 5.96019 8.99686 5.87814C8.91481 5.79609 8.80353 5.75 8.6875 5.75C8.57147 5.75 8.46019 5.79609 8.37814 5.87814C8.29609 5.96019 8.25 6.07147 8.25 6.1875V11.4375C8.25 11.5535 8.29609 11.6648 8.37814 11.7469C8.46019 11.8289 8.57147 11.875 8.6875 11.875C8.80353 11.875 8.91481 11.8289 8.99686 11.7469C9.07891 11.6648 9.125 11.5535 9.125 11.4375V6.1875Z"
                    fill="#DC3545"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.1875 3.5625C12.1875 3.79456 12.0953 4.01712 11.9312 4.18122C11.7671 4.34531 11.5446 4.4375 11.3125 4.4375H10.875V12.3125C10.875 12.7766 10.6906 13.2217 10.3624 13.5499C10.0342 13.8781 9.58913 14.0625 9.125 14.0625H3.875C3.41087 14.0625 2.96575 13.8781 2.63756 13.5499C2.30937 13.2217 2.125 12.7766 2.125 12.3125V4.4375H1.6875C1.45544 4.4375 1.23288 4.34531 1.06878 4.18122C0.904687 4.01712 0.8125 3.79456 0.8125 3.5625V2.6875C0.8125 2.45544 0.904687 2.23288 1.06878 2.06878C1.23288 1.90469 1.45544 1.8125 1.6875 1.8125H4.75C4.75 1.58044 4.84219 1.35788 5.00628 1.19378C5.17038 1.02969 5.39294 0.9375 5.625 0.9375H7.375C7.60706 0.9375 7.82962 1.02969 7.99372 1.19378C8.15781 1.35788 8.25 1.58044 8.25 1.8125H11.3125C11.5446 1.8125 11.7671 1.90469 11.9312 2.06878C12.0953 2.23288 12.1875 2.45544 12.1875 2.6875V3.5625ZM3.10325 4.4375L3 4.48913V12.3125C3 12.5446 3.09219 12.7671 3.25628 12.9312C3.42038 13.0953 3.64294 13.1875 3.875 13.1875H9.125C9.35707 13.1875 9.57962 13.0953 9.74372 12.9312C9.90781 12.7671 10 12.5446 10 12.3125V4.48913L9.89675 4.4375H3.10325ZM1.6875 3.5625V2.6875H11.3125V3.5625H1.6875Z"
                    fill="#DC3545"
                  />
                </svg>
              </button>
            </div>
          </div>

          {preview ? (
            <div
              className=""
              style={{ marginTop: "-25px", position: "relative" }}
            >
              <button
                type="button"
                className="icon-button"
                title="Fullscreen"
                onClick={() => {
                  const videoElement = document.querySelector("video");
                  if (videoElement) {
                    if (document.fullscreenElement) {
                      document.exitFullscreen();
                    } else {
                      videoElement.requestFullscreen();
                    }
                  }
                }}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  zIndex: "10",
                  background: "rgba(0, 0, 0, 0.5)",
                  border: "none",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 20 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ filter: "brightness(0) invert(1)" }}
                >
                  <path
                    d="M0 13.4124L7.39648 13.0675L3.39952 6.8344L0 13.4124ZM20 0.587402L12.6035 0.932333L16.6005 7.16541L20 0.587402ZM5.20435 10.8369L15.4879 4.24252L14.7957 3.16292L4.51205 9.75728L5.20435 10.8369Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <video
                key={videoKey}
                controls
                className=""
                style={{
                  width: "100%",
                  maxHeight: "500px",
                  border: "1px solid #ccc",
                  objectFit: "contain",
                }}
              >
                <source src={preview} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <label
              htmlFor="fileInput"
              className="upload-prompt"
              style={{
                textAlign: "center",
                cursor: !canEdit ? "not-allowed" : "pointer",
                opacity: !canEdit ? 0.5 : 1,
                pointerEvents: !canEdit ? "none" : "auto",
              }}
              title={
                !canEdit
                  ? "You do not have permission to upload videos."
                  : undefined
              }
            >
              <svg
                width="17"
                height="15"
                viewBox="0 0 17 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 9.45021C1.13261 9.45021 1.25979 9.50289 1.35355 9.59666C1.44732 9.69042 1.5 9.8176 1.5 9.95021V12.4502C1.5 12.7154 1.60536 12.9698 1.79289 13.1573C1.98043 13.3449 2.23478 13.4502 2.5 13.4502H14.5C14.7652 13.4502 15.0196 13.3449 15.2071 13.1573C15.3946 12.9698 15.5 12.7154 15.5 12.4502V9.95021C15.5 9.8176 15.5527 9.69042 15.6464 9.59666C15.7402 9.50289 15.8674 9.45021 16 9.45021C16.1326 9.45021 16.2598 9.50289 16.3536 9.59666C16.4473 9.69042 16.5 9.8176 16.5 9.95021V12.4502C16.5 12.9806 16.2893 13.4894 15.9142 13.8644C15.5391 14.2395 15.0304 14.4502 14.5 14.4502H2.5C1.96957 14.4502 1.46086 14.2395 1.08579 13.8644C0.710714 13.4894 0.5 12.9806 0.5 12.4502V9.95021C0.5 9.8176 0.552678 9.69042 0.646447 9.59666C0.740215 9.50289 0.867392 9.45021 1 9.45021Z"
                  fill="#0D6EFD"
                />
                <path
                  d="M8.146 0.69621C8.19245 0.649647 8.24762 0.612704 8.30837 0.587498C8.36911 0.562291 8.43423 0.549316 8.5 0.549316C8.56577 0.549316 8.63089 0.562291 8.69163 0.587498C8.75238 0.612704 8.80755 0.649647 8.854 0.69621L11.854 3.69621C11.9479 3.7901 12.0006 3.91743 12.0006 4.05021C12.0006 4.18299 11.9479 4.31032 11.854 4.40421C11.7601 4.4981 11.6328 4.55084 11.5 4.55084C11.3672 4.55084 11.2399 4.4981 11.146 4.40421L9 2.25721V11.0502C9 11.1828 8.94732 11.31 8.85355 11.4038C8.75979 11.4975 8.63261 11.5502 8.5 11.5502C8.36739 11.5502 8.24021 11.4975 8.14645 11.4038C8.05268 11.31 8 11.1828 8 11.0502V2.25721L5.854 4.40421C5.80751 4.4507 5.75232 4.48757 5.69158 4.51273C5.63084 4.53789 5.56574 4.55084 5.5 4.55084C5.43426 4.55084 5.36916 4.53789 5.30842 4.51273C5.24768 4.48757 5.19249 4.4507 5.146 4.40421C5.09951 4.35772 5.06264 4.30253 5.03748 4.24179C5.01232 4.18105 4.99937 4.11595 4.99937 4.05021C4.99937 3.98447 5.01232 3.91937 5.03748 3.85863C5.06264 3.79789 5.09951 3.7427 5.146 3.69621L8.146 0.69621Z"
                  fill="#0D6EFD"
                />
              </svg>

              <div style={{ fontWeight: "500" }}>
                Click or drag file to this area to upload
              </div>
              <p style={{ fontSize: "0.875rem", color: "#777" }}>
                Supports only a single video. Maximum file size {maxSizeMb}MB.
              </p>
            </label>
          )}
        </div>

        <VideoFileInput
          id="fileInput"
          accept={fileAccept}
          capture={fileCapture}
          onChange={onFileChange}
          hidden
          disabled={!canEdit}
        />
      </div>

      <div className="form-row">
        <TextField
          label="ID (Read Only)"
          value={idValue}
          readOnly
          onChange={onIdChange}
        />

        <div className="form-group">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <label className="block font-semibold mt-4">Video URL</label>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              style={{
                marginRight: "11px",
                cursor: "pointer",
                fill: "#007bff",
              }}
              onClick={handleOpenVideoUrl}
              role="button"
              title="Open URL in new tab"
            >
              <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
            </svg>
          </div>
          <TextField
            label={null}
            value={videoUrl}
            placeholder="Enter Video URL"
            width="100%"
            style={{ marginTop: "-5px", marginLeft: "-2px" }}
            onChange={onVideoUrlChange}
            className="w-full"
          />
        </div>
      </div>

      <div className="form-group" style={{ marginLeft: "10px", marginRight: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <label className="block font-semibold mt-4">Short Description</label>
        </div>
        <ShortDescriptionEditor
          value={shortDescription}
          onChange={onShortDescriptionChange}
          onDirtyChange={onShortDescriptionDirtyChange}
          placeholder="Enter short description"
          minHeight="100px"
        />
      </div>
      <div style={{ height: "30px" }}></div>
      <SaveModal
        isOpen={saveModal.isOpen}
        onClose={saveModal.onClose}
        onSave={saveModal.onSave}
        title={saveModal.title}
        subtitle={saveModal.subtitle}
        editorName={saveModal.editorName}
        comment={saveModal.comment}
        onCommentChange={saveModal.onCommentChange}
        isLoading={saveModal.isLoading}
        showLoader={saveModal.showLoader}
      />
    </div>
  );
};

export default VideoFormFields;
